import { useEffect } from 'react';
import { seoConfig, buildImageUrl } from '../../config/seo';

const SEO = ({
  title = seoConfig.defaultTitle,
  description = seoConfig.defaultDescription,
  keywords = seoConfig.defaultKeywords,
  ogImage = seoConfig.defaultOgImage,
  url = seoConfig.siteUrl,
  type = 'website',
  structuredData = null,
}) => {
  useEffect(() => {
    // Update title
    document.title = title;

    // Helper function to update or create meta tags
    const updateMetaTag = (selector, attribute, value) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');

        // Safely extract attribute name and value
        const match = selector.match(/["']([^"']+)["']/);
        if (!match) {
          console.error(`Invalid selector: ${selector}`);
          return;
        }

        const attrName = selector.includes('property=') ? 'property' : 'name';
        const attrValue = match[1];
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, value);
    };

    const fullImageUrl = buildImageUrl(ogImage);

    // Update primary meta tags
    updateMetaTag('meta[name="title"]', 'content', title);
    updateMetaTag('meta[name="description"]', 'content', description);
    updateMetaTag('meta[name="keywords"]', 'content', keywords);

    // Update Open Graph tags
    updateMetaTag('meta[property="og:type"]', 'content', type);
    updateMetaTag('meta[property="og:url"]', 'content', url);
    updateMetaTag('meta[property="og:title"]', 'content', title);
    updateMetaTag('meta[property="og:description"]', 'content', description);
    updateMetaTag('meta[property="og:image"]', 'content', fullImageUrl);
    updateMetaTag('meta[property="og:site_name"]', 'content', seoConfig.siteName);

    // Update Twitter tags (use 'name' not 'property')
    updateMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
    updateMetaTag('meta[name="twitter:url"]', 'content', url);
    updateMetaTag('meta[name="twitter:title"]', 'content', title);
    updateMetaTag('meta[name="twitter:description"]', 'content', description);
    updateMetaTag('meta[name="twitter:image"]', 'content', fullImageUrl);

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // Update structured data if provided
    let dynamicScript = null;
    if (structuredData) {
      dynamicScript = document.querySelector('script[data-dynamic-ld]');
      if (!dynamicScript) {
        dynamicScript = document.createElement('script');
        dynamicScript.setAttribute('type', 'application/ld+json');
        dynamicScript.setAttribute('data-dynamic-ld', 'true');
        document.head.appendChild(dynamicScript);
      }
      dynamicScript.textContent = JSON.stringify(structuredData);
    }

    // Cleanup function
    return () => {
      // Remove dynamic structured data on unmount
      if (dynamicScript && dynamicScript.parentNode) {
        dynamicScript.parentNode.removeChild(dynamicScript);
      }
    };
  }, [title, description, keywords, ogImage, url, type, structuredData]);

  return null;
};

export default SEO;
